import { useState, useEffect } from 'react'
import { format, eachDayOfInterval, startOfDay } from 'date-fns'
import { es } from 'date-fns/locale'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { LuChartBar, LuRefreshCw, LuSheet, LuChevronLeft, LuChevronRight, LuLoader, LuList, LuX, LuArrowDown, LuArrowUp, LuWallet } from 'react-icons/lu'
import api from '../services/api'
import './ConsolidacionPage.css'

// Registrar locale español para el datepicker
registerLocale('es', es)

// Límite alto para obtener suficientes notificaciones en UNA request
const NOTIFICATIONS_LIMIT = 10000

// Registros por página en la UI
const PAGE_SIZE = 100

// Cantidad de consultas en paralelo (reducido para evitar sobrecarga)
const PARALLEL_REQUESTS = 5

// Función para obtener notificaciones de un CVU
const fetchNotificationsForCvu = async (cvu) => {
  const response = await api.get('/notifications', {
    params: {
      cvu: cvu,
      limit: NOTIFICATIONS_LIMIT
    }
  })
  return response.data.notifications || []
}

// Función para formatear monto con puntos de miles (formato argentino)
const formatAmount = (value) => {
  const num = parseFloat(value) || 0
  return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Función para formatear fecha/hora de notificación
const formatNotificationDate = (dateString) => {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString.replace(' ', 'T'))
    return format(date, 'dd/MM/yyyy HH:mm:ss', { locale: es })
  } catch (e) {
    return dateString
  }
}

function ConsolidacionPage() {
  // Estado para modo activo: null = mostrar ambas cards, 'saldos' o 'movimientos'
  const [activeMode, setActiveMode] = useState(null)
  
  // ============ Estados para SALDOS ACTUALES (card 1) ============
  const [saldosLoading, setSaldosLoading] = useState(false)
  const [saldosPageLoading, setSaldosPageLoading] = useState(false)
  const [saldosError, setSaldosError] = useState('')
  const [allAccounts, setAllAccounts] = useState([]) // Lista completa de cuentas
  const [saldosData, setSaldosData] = useState({}) // Map de accountNumber -> saldo
  const [saldosProcessed, setSaldosProcessed] = useState(false)
  const [saldosCurrentPage, setSaldosCurrentPage] = useState(1)
  
  // ============ Estados para MOVIMIENTOS (card 2) ============
  const [movFromDate, setMovFromDate] = useState(null)
  const [movToDate, setMovToDate] = useState(null)
  const [movLoading, setMovLoading] = useState(false)
  const [movProgress, setMovProgress] = useState({ current: 0, total: 0 })
  const [movError, setMovError] = useState('')
  const [movData, setMovData] = useState([])
  const [movProcessed, setMovProcessed] = useState(false)
  const [movCurrentPage, setMovCurrentPage] = useState(1)
  
  // ============ SALDOS: Cálculos de paginación ============
  const saldosTotalPages = Math.ceil(allAccounts.length / PAGE_SIZE)
  const saldosStartIndex = (saldosCurrentPage - 1) * PAGE_SIZE
  const saldosEndIndex = saldosStartIndex + PAGE_SIZE
  const currentPageAccounts = allAccounts.slice(saldosStartIndex, saldosEndIndex)
  
  // ============ SALDOS: Cargar saldos de la página actual ============
  const loadSaldosForCurrentPage = async () => {
    if (currentPageAccounts.length === 0) return
    
    // Verificar cuáles cuentas de esta página ya tienen saldo cargado
    const accountsToLoad = currentPageAccounts.filter(acc => !saldosData[acc.account_number])
    
    if (accountsToLoad.length === 0) return // Ya están todos cargados
    
    setSaldosPageLoading(true)
    setSaldosError('')
    
    try {
      console.log(`💰 Cargando saldos de ${accountsToLoad.length} cuentas (página ${saldosCurrentPage})...`)
      
      // Consultar saldos en paralelo (de a PARALLEL_REQUESTS)
      const newSaldos = { ...saldosData }
      
      for (let i = 0; i < accountsToLoad.length; i += PARALLEL_REQUESTS) {
        const batch = accountsToLoad.slice(i, i + PARALLEL_REQUESTS)
        
        const results = await Promise.all(
          batch.map(async (acc) => {
            try {
              // Usar POST /accounts/balance con el token del usuario logueado
              const response = await api.post('/accounts/balance', {
                accountNumber: acc.account_number
              })
              if (response.data.success && response.data.data) {
                const data = response.data.data
                // El saldo disponible está en available_for_purchases
                return {
                  accountNumber: acc.account_number,
                  success: true,
                  available: parseFloat(data.available_for_purchases) || 0,
                  balance: parseFloat(data.balance) || 0,
                  reserveBalance: parseFloat(data.reserve_balance) || 0
                }
              } else {
                return {
                  accountNumber: acc.account_number,
                  success: false,
                  error: response.data.error || response.data.description || 'Error'
                }
              }
            } catch (err) {
              return {
                accountNumber: acc.account_number,
                success: false,
                error: err.response?.data?.error || err.message
              }
            }
          })
        )
        
        // Guardar resultados
        results.forEach(result => {
          if (result.success) {
            newSaldos[result.accountNumber] = {
              available: result.available,
              balance: result.balance,
              reserveBalance: result.reserveBalance
            }
          } else {
            newSaldos[result.accountNumber] = {
              error: result.error
            }
          }
        })
      }
      
      setSaldosData(newSaldos)
      console.log(`✅ Saldos cargados para página ${saldosCurrentPage}`)
      
    } catch (err) {
      console.error('Error cargando saldos:', err)
      setSaldosError('Error al cargar saldos')
    } finally {
      setSaldosPageLoading(false)
    }
  }
  
  // Cargar saldos cuando cambia la página
  useEffect(() => {
    if (saldosProcessed && currentPageAccounts.length > 0) {
      loadSaldosForCurrentPage()
    }
  }, [saldosCurrentPage, saldosProcessed])
  
  // ============ SALDOS: Procesar (obtener lista de cuentas) ============
  const handleSaldosProcess = async () => {
    setActiveMode('saldos')
    setSaldosLoading(true)
    setSaldosError('')
    setAllAccounts([])
    setSaldosData({})
    setSaldosProcessed(false)
    setSaldosCurrentPage(1)
    
    try {
      // 1. Obtener todos los CVUs/cuentas del backend
      console.log('📊 [Saldos] Obteniendo lista de cuentas...')
      const cvusResponse = await api.get('/accounts/all-cvus')
      const allCvus = cvusResponse.data.cvus || []
      
      if (allCvus.length === 0) {
        setSaldosError('No se encontraron cuentas en la plataforma')
        setSaldosLoading(false)
        return
      }
      
      console.log(`✅ [Saldos] Encontradas ${allCvus.length} cuentas`)
      
      // Guardar lista de cuentas
      setAllAccounts(allCvus)
      setSaldosProcessed(true)
      
    } catch (err) {
      console.error('Error obteniendo cuentas:', err)
      setSaldosError(err.response?.data?.error || 'Error al obtener la lista de cuentas')
    } finally {
      setSaldosLoading(false)
    }
  }
  
  // ============ SALDOS: Cambio de página ============
  const handleSaldosPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= saldosTotalPages) {
      setSaldosCurrentPage(newPage)
    }
  }
  
  // ============ SALDOS: Exportar ============
  const handleSaldosExport = () => {
    if (allAccounts.length === 0) return
    
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return ''
      let strValue = String(value)
      if (strValue.includes(';') || strValue.includes('"') || strValue.includes('\n')) {
        return `"${strValue.replace(/"/g, '""')}"`
      }
      return strValue
    }
    
    const formatAmountCSV = (amount) => {
      if (typeof amount !== 'number') amount = parseFloat(amount) || 0
      return amount.toFixed(2).replace('.', ',')
    }
    
    const now = new Date()
    const fechaHora = format(now, 'dd/MM/yyyy HH:mm:ss')
    
    let csvContent = `"Saldos Actuales de Todas las Cuentas"\n`
    csvContent += `"Fecha de consulta: ${fechaHora}"\n\n`
    csvContent += `CVU;Cuenta;Saldo Disponible\n`
    
    allAccounts.forEach(acc => {
      const saldo = saldosData[acc.account_number]
      if (saldo && !saldo.error) {
        csvContent += `${escapeCSV(acc.cvu)};${escapeCSV(acc.account_number)};`
        csvContent += `${formatAmountCSV(saldo.available)}\n`
      } else {
        csvContent += `${escapeCSV(acc.cvu)};${escapeCSV(acc.account_number)};-\n`
      }
    })
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.setAttribute('href', URL.createObjectURL(blob))
    link.setAttribute('download', `saldos_actuales_${format(now, 'yyyy-MM-dd_HH-mm')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  
  // ============ MOVIMIENTOS: Handlers ============
  const handleMovFromDateChange = (date) => {
    setMovFromDate(date)
    if (movToDate && date && movToDate < date) {
      setMovToDate(null)
    }
  }
  
  const handleMovToDateChange = (date) => {
    if (movFromDate && date && date < movFromDate) return
    setMovToDate(date)
  }
  
  const handleMovProcess = async () => {
    if (!movFromDate || !movToDate) {
      setMovError('Debe seleccionar ambas fechas')
      return
    }
    
    setActiveMode('movimientos')
    setMovLoading(true)
    setMovError('')
    setMovData([])
    setMovProcessed(false)
    setMovCurrentPage(1)
    
    try {
      console.log('📋 [Movimientos] Obteniendo todos los CVUs...')
      const cvusResponse = await api.get('/accounts/all-cvus')
      const allCvus = cvusResponse.data.cvus || []
      
      if (allCvus.length === 0) {
        setMovError('No se encontraron CVUs en la plataforma')
        setMovLoading(false)
        return
      }
      
      console.log(`✅ [Movimientos] Encontrados ${allCvus.length} CVUs`)
      
      const fromDateStart = new Date(movFromDate)
      fromDateStart.setHours(0, 0, 0, 0)
      const toDateEnd = new Date(movToDate)
      toDateEnd.setHours(23, 59, 59, 999)
      
      setMovProgress({ current: 0, total: allCvus.length })
      let allMovements = []
      
      // Procesar en paralelo por batches
      for (let i = 0; i < allCvus.length; i += PARALLEL_REQUESTS) {
        const batch = allCvus.slice(i, i + PARALLEL_REQUESTS)
        
        const batchResults = await Promise.all(
          batch.map(async (cvuData) => {
            try {
              const notifications = await fetchNotificationsForCvu(cvuData.cvu)
              const cvuMovements = []
              
              notifications.forEach(notif => {
                const txData = notif.transaction_data || {}
                const datetime = txData.datetime || ''
                
                if (!datetime) return
                
                const notifDate = new Date(datetime.replace(' ', 'T'))
                if (notifDate < fromDateStart || notifDate > toDateEnd) return
                
                cvuMovements.push({
                  id: notif.id,
                  datetime: datetime,
                  cvu: cvuData.cvu,
                  cuenta: cvuData.account_number,
                  tipo: txData.transfer_type,
                  monto: parseFloat(txData.amount) || 0,
                  origen: txData.cardholder_from || '-',
                  cvuOrigen: txData.cbu_from || '-',
                  cuitOrigen: txData.origin_tax_id || '-',
                  destino: txData.cardholder_to || '-',
                  cvuDestino: txData.cbu_to || '-',
                  authCode: txData.authorization_code || '-'
                })
              })
              
              return cvuMovements
            } catch (cvuError) {
              console.error(`Error consultando CVU ${cvuData.cvu}:`, cvuError.message)
              return []
            }
          })
        )
        
        allMovements = allMovements.concat(batchResults.flat())
        setMovProgress({ current: Math.min(i + PARALLEL_REQUESTS, allCvus.length), total: allCvus.length })
      }
      
      // Ordenar por fecha descendente
      allMovements.sort((a, b) => {
        const dateA = new Date(a.datetime.replace(' ', 'T'))
        const dateB = new Date(b.datetime.replace(' ', 'T'))
        return dateB.getTime() - dateA.getTime()
      })
      
      console.log(`✅ [Movimientos] Total: ${allMovements.length} movimientos`)
      
      setMovData(allMovements)
      setMovProcessed(true)
      
    } catch (err) {
      console.error('Error obteniendo movimientos:', err)
      setMovError(err.response?.data?.error || 'Error al obtener los movimientos')
    } finally {
      setMovLoading(false)
      setMovProgress({ current: 0, total: 0 })
    }
  }
  
  const handleMovExport = () => {
    if (movData.length === 0) return
    
    const fromDateStr = format(movFromDate, 'dd/MM/yyyy')
    const toDateStr = format(movToDate, 'dd/MM/yyyy')
    
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return ''
      let strValue = String(value)
      if (strValue.includes(';') || strValue.includes('"') || strValue.includes('\n')) {
        return `"${strValue.replace(/"/g, '""')}"`
      }
      return strValue
    }
    
    const formatAmountCSV = (amount) => {
      if (typeof amount !== 'number') amount = parseFloat(amount) || 0
      return amount.toFixed(2).replace('.', ',')
    }
    
    let csvContent = `"Todos los Movimientos de Todos los CVUs"\n`
    csvContent += `"Período: ${fromDateStr} - ${toDateStr}"\n`
    csvContent += `"Total: ${movData.length} movimientos"\n\n`
    csvContent += `Fecha/Hora;CVU;Cuenta;Tipo;Origen;CUIT Origen;CVU Origen;Destino;CVU Destino;Monto;Cód. Autorización\n`
    
    movData.forEach(mov => {
      const fecha = formatNotificationDate(mov.datetime)
      const tipo = mov.tipo === 'E' ? 'Entrante' : 'Saliente'
      const monto = mov.tipo === 'E' ? formatAmountCSV(mov.monto) : formatAmountCSV(-mov.monto)
      
      csvContent += `${escapeCSV(fecha)};${escapeCSV(mov.cvu)};${escapeCSV(mov.cuenta)};${escapeCSV(tipo)};`
      csvContent += `${escapeCSV(mov.origen)};${escapeCSV(mov.cuitOrigen)};${escapeCSV(mov.cvuOrigen)};${escapeCSV(mov.destino)};${escapeCSV(mov.cvuDestino)};`
      csvContent += `${monto};${escapeCSV(mov.authCode)}\n`
    })
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.setAttribute('href', URL.createObjectURL(blob))
    link.setAttribute('download', `movimientos_todos_${format(movFromDate, 'yyyy-MM-dd')}_${format(movToDate, 'yyyy-MM-dd')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  
  // ============ CANCELAR / VOLVER ============
  const handleCancel = () => {
    setActiveMode(null)
    // Reset saldos
    setSaldosProcessed(false)
    setAllAccounts([])
    setSaldosData({})
    setSaldosError('')
    setSaldosCurrentPage(1)
    // Reset movimientos
    setMovProcessed(false)
    setMovData([])
    setMovError('')
    setMovCurrentPage(1)
  }
  
  // ============ PAGINACIÓN MOVIMIENTOS ============
  const movTotalPages = Math.ceil(movData.length / PAGE_SIZE)
  const movStartIndex = (movCurrentPage - 1) * PAGE_SIZE
  const movEndIndex = movStartIndex + PAGE_SIZE
  const movPaginatedData = movData.slice(movStartIndex, movEndIndex)
  
  return (
    <div className="consolidacion-page">
      <div className="page-header">
        <h1><LuChartBar /> Consolidación</h1>
      </div>
      
      {/* ============ CARD 1: SALDOS ACTUALES ============ */}
      {(activeMode === null || activeMode === 'saldos') && (
        <div className={`option-card ${activeMode === 'saldos' ? 'active' : ''}`}>
          <div className="option-card-header">
            <LuWallet className="option-icon" />
            <h3>Ver saldo actual de cada cuenta</h3>
            {activeMode === 'saldos' && (
              <button className="btn-cancel" onClick={handleCancel} title="Cancelar">
                <LuX />
              </button>
            )}
          </div>
          <div className="option-card-body">
            <div className="option-filters">
              <button
                className="btn-process"
                onClick={handleSaldosProcess}
                disabled={saldosLoading}
              >
                {saldosLoading ? (
                  <><LuLoader className="spinner-icon" /> Obteniendo cuentas...</>
                ) : (
                  <><LuRefreshCw /> Procesar</>
                )}
              </button>
              {saldosProcessed && allAccounts.length > 0 && (
                <button className="btn-export" onClick={handleSaldosExport} disabled={saldosLoading || saldosPageLoading}>
                  <LuSheet /> Exportar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* ============ CARD 2: MOVIMIENTOS ============ */}
      {(activeMode === null || activeMode === 'movimientos') && (
        <div className={`option-card ${activeMode === 'movimientos' ? 'active' : ''}`}>
          <div className="option-card-header">
            <LuList className="option-icon" />
            <h3>Obtener todos los movimientos de todos los CVUs</h3>
            {activeMode === 'movimientos' && (
              <button className="btn-cancel" onClick={handleCancel} title="Cancelar">
                <LuX />
              </button>
            )}
          </div>
          <div className="option-card-body">
            <div className="option-filters">
              <div className="filter-group">
                <label>Desde</label>
                <DatePicker
                  selected={movFromDate}
                  onChange={handleMovFromDateChange}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  placeholderText="Seleccionar fecha"
                  className="form-control date-input"
                  maxDate={new Date()}
                  disabled={movLoading}
                />
              </div>
              <div className="filter-group">
                <label>Hasta</label>
                <DatePicker
                  selected={movToDate}
                  onChange={handleMovToDateChange}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  placeholderText="Seleccionar fecha"
                  className="form-control date-input"
                  minDate={movFromDate}
                  maxDate={new Date()}
                  disabled={!movFromDate || movLoading}
                />
              </div>
              <button
                className="btn-process"
                onClick={handleMovProcess}
                disabled={movLoading || !movFromDate || !movToDate}
              >
                {movLoading ? (
                  <><LuLoader className="spinner-icon" /> Procesando...</>
                ) : (
                  <><LuRefreshCw /> Procesar</>
                )}
              </button>
              {movProcessed && movData.length > 0 && (
                <button className="btn-export" onClick={handleMovExport} disabled={movLoading}>
                  <LuSheet /> Exportar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* ============ PROGRESO DE CARGA MOVIMIENTOS ============ */}
      {movLoading && movProgress.total > 0 && (
        <div className="loading-progress">
          <LuLoader className="spinner-icon" />
          <span>Cargando CVU {movProgress.current} de {movProgress.total}...</span>
        </div>
      )}
      
      {/* ============ ERRORES ============ */}
      {saldosError && <div className="error-message">{saldosError}</div>}
      {movError && <div className="error-message">{movError}</div>}
      
      {/* ============ RESULTADOS SALDOS ============ */}
      {activeMode === 'saldos' && saldosProcessed && !saldosLoading && (
        <div className="consolidacion-results">
          {allAccounts.length === 0 ? (
            <div className="no-results">No se encontraron cuentas</div>
          ) : (
            <>
              <div className="results-summary">
                Mostrando {saldosStartIndex + 1}-{Math.min(saldosEndIndex, allAccounts.length)} de {allAccounts.length.toLocaleString('es-AR')} cuentas
                {saldosPageLoading && <span className="loading-inline"><LuLoader className="spinner-icon" /> Cargando saldos...</span>}
              </div>
              
              <div className="table-container">
                <table className="consolidacion-table">
                  <thead>
                    <tr>
                      <th>CVU</th>
                      <th>Cuenta</th>
                      <th>Saldo Disponible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageAccounts.map((acc) => {
                      const saldo = saldosData[acc.account_number]
                      const isLoading = !saldo && saldosPageLoading
                      const hasError = saldo?.error
                      
                      return (
                        <tr key={acc.account_number}>
                          <td className="cvu-cell">{acc.cvu}</td>
                          <td>{acc.account_number}</td>
                          <td className={`amount-cell ${saldo?.available > 0 ? 'amount-positive' : ''}`}>
                            {isLoading ? (
                              <span className="loading-cell"><LuLoader className="spinner-icon-small" /></span>
                            ) : hasError ? (
                              <span className="error-cell">Error</span>
                            ) : saldo ? (
                              `$${formatAmount(saldo.available)}`
                            ) : (
                              <span className="pending-cell">-</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              
              {saldosTotalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn-page"
                    onClick={() => handleSaldosPageChange(saldosCurrentPage - 1)}
                    disabled={saldosCurrentPage === 1 || saldosPageLoading}
                  >
                    <LuChevronLeft /> Anterior
                  </button>
                  <span className="page-info">Página {saldosCurrentPage} de {saldosTotalPages}</span>
                  <button
                    className="btn-page"
                    onClick={() => handleSaldosPageChange(saldosCurrentPage + 1)}
                    disabled={saldosCurrentPage === saldosTotalPages || saldosPageLoading}
                  >
                    Siguiente <LuChevronRight />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* ============ RESULTADOS MOVIMIENTOS ============ */}
      {activeMode === 'movimientos' && movProcessed && !movLoading && (
        <div className="consolidacion-results">
          {movData.length === 0 ? (
            <div className="no-results">No se encontraron movimientos para el período seleccionado</div>
          ) : (
            <>
              <div className="results-summary">
                Mostrando {movStartIndex + 1}-{Math.min(movEndIndex, movData.length)} de {movData.length.toLocaleString('es-AR')} movimientos
              </div>
              
              <div className="table-container">
                <table className="consolidacion-table movimientos-table">
                  <thead>
                    <tr>
                      <th>Fecha/Hora</th>
                      <th>CVU</th>
                      <th>Cuenta</th>
                      <th>Tipo</th>
                      <th>Origen</th>
                      <th>Destino</th>
                      <th>Monto</th>
                      <th>Cód. Auth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movPaginatedData.map((mov, index) => (
                      <tr key={`${mov.id}-${index}`}>
                        <td className="datetime-cell">{formatNotificationDate(mov.datetime)}</td>
                        <td className="cvu-cell">{mov.cvu}</td>
                        <td>{mov.cuenta}</td>
                        <td className={`tipo-cell ${mov.tipo === 'E' ? 'entrante' : 'saliente'}`}>
                          {mov.tipo === 'E' ? (
                            <span><LuArrowDown className="tipo-icon" />E</span>
                          ) : (
                            <span><LuArrowUp className="tipo-icon" />S</span>
                          )}
                        </td>
                        <td>
                          <div className="cell-name">{mov.origen}</div>
                          <div className="cell-cvu">{mov.cvuOrigen !== '-' ? mov.cvuOrigen : ''}</div>
                        </td>
                        <td>
                          <div className="cell-name">{mov.destino}</div>
                          <div className="cell-cvu">{mov.cvuDestino !== '-' ? mov.cvuDestino : ''}</div>
                        </td>
                        <td className={`amount-cell ${mov.tipo === 'E' ? 'amount-positive' : 'amount-negative'}`}>
                          {mov.tipo === 'E' ? '+' : '-'}${formatAmount(mov.monto)}
                        </td>
                        <td className="auth-cell">{mov.authCode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {movTotalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn-page"
                    onClick={() => setMovCurrentPage(movCurrentPage - 1)}
                    disabled={movCurrentPage === 1}
                  >
                    <LuChevronLeft /> Anterior
                  </button>
                  <span className="page-info">Página {movCurrentPage} de {movTotalPages}</span>
                  <button
                    className="btn-page"
                    onClick={() => setMovCurrentPage(movCurrentPage + 1)}
                    disabled={movCurrentPage === movTotalPages}
                  >
                    Siguiente <LuChevronRight />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default ConsolidacionPage
